import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { keywordVolumeService } from "@/services/KeywordVolumeService";

const SOURCES_KEY = ["keywordVolumeSources"] as const;
const INTERVALS = [1, 2, 3, 6, 12];

/**
 * Google Sheet links read by sync-keyword-volumes. The sheet has to be shared
 * with the project's Google service account (the one Domain Tracker uses).
 */
export function KeywordVolumeSources() {
  const qc = useQueryClient();
  const [url, setUrl] = useState("");
  const [sheetName, setSheetName] = useState("Monthly Data");
  const [interval, setInterval] = useState("1");
  const [error, setError] = useState<string | null>(null);

  const { data: sources = [] } = useQuery({
    queryKey: SOURCES_KEY,
    queryFn: () => keywordVolumeService.listSources(),
  });

  const done = () => {
    setError(null);
    void qc.invalidateQueries({ queryKey: SOURCES_KEY });
    void qc.invalidateQueries({ queryKey: ["keywordVolumesWithDomains"] });
    void qc.invalidateQueries({ queryKey: ["keywordVolumesForRows"] });
  };
  const onError = (e: Error) => setError(e.message);

  const save = useMutation({
    mutationFn: () => keywordVolumeService.saveSource(url, sheetName, Number(interval)),
    onSuccess: () => {
      setUrl("");
      done();
    },
    onError,
  });
  const remove = useMutation({ mutationFn: (id: number) => keywordVolumeService.removeSource(id), onSuccess: done, onError });
  const sync = useMutation({ mutationFn: (id: number) => keywordVolumeService.syncSource(id), onSuccess: done, onError });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Google Sheet sources</CardTitle>
        <CardDescription>
          Read automatically every N months (default 1). Share the sheet with the project's service
          account first.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <Input
            placeholder="https://docs.google.com/spreadsheets/d/…"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            className="min-w-64 flex-1"
          />
          <Input
            value={sheetName}
            onChange={(e) => setSheetName(e.target.value)}
            className="w-36"
            aria-label="Sheet name"
          />
          <Select value={interval} onValueChange={setInterval}>
            <SelectTrigger className="w-32" aria-label="Update every">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {INTERVALS.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  every {n} mo
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button disabled={!url.includes("/spreadsheets/d/") || save.isPending} onClick={() => save.mutate()}>
            Add
          </Button>
        </div>

        {error && <p className="text-sm signal-text-negative">{error}</p>}

        {sources.map((s) => (
          <div key={s.id} className="flex items-center justify-between gap-2 rounded border p-2 text-sm">
            <div className="min-w-0">
              <div className="truncate" title={s.sheet_url}>
                {s.sheet_name} · every {s.interval_months} mo
              </div>
              <div className="text-xs text-muted-foreground">
                {s.last_synced_at ? `synced ${new Date(s.last_synced_at).toLocaleString()}` : "never synced"}
                {s.last_error && <span className="signal-text-negative"> · {s.last_error}</span>}
              </div>
            </div>
            <div className="flex shrink-0 gap-1">
              <Button size="sm" variant="secondary" disabled={sync.isPending} onClick={() => sync.mutate(s.id)}>
                Sync now
              </Button>
              <Button size="sm" variant="ghost" disabled={remove.isPending} onClick={() => remove.mutate(s.id)}>
                Remove
              </Button>
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
